const { SEPHORA_URL: { DESKTOP } } = require('components/constants');
const { SpaTemplatesInfo } = require('constants/SpaTemplatesInfo');
const bccUtil = require('utils/BCC');
const LocationUtils = require('utils/Location');
const store = require('Store');
const UrlUtils = require('utils/Url');
const UserActions = require('actions/UserActions');
const PageTemplateType = require('constants/PageTemplateType');
const reject = require('utils/functions/reject');
const { capitalize } = require('utils/String');

const SpaUtils = {
    replaceContentAreaTargeters: function (currentData = {}, newData = {}) {
        let allPageTargeterNames = Sephora.targetersToInclude.substr('?includeTargeters='.length);
        allPageTargeterNames = decodeURIComponent(allPageTargeterNames).split(',');

        // remove old content area targeters
        const currentTargeterNames = bccUtil.extractTargeters(currentData);
        for (const targeterName of currentTargeterNames) {
            const index = allPageTargeterNames.indexOf(targeterName);
            if (index !== -1) {
                allPageTargeterNames.splice(index, 1);
            }
        }

        // add new content area targeters
        const newTargeterNames = bccUtil.extractTargeters(newData);
        allPageTargeterNames = allPageTargeterNames.concat(newTargeterNames);
        Sephora.targetersToInclude = bccUtil.buildTargetersQueryParams(allPageTargeterNames);
    },

    updateUserFull: function (data, productPageData = {}, options = {}) {
        const currentUser = Sephora.Util.getCurrentUser();
        const basketCache = Sephora.Util.getBasketCache(currentUser);
        const userInfoCache = Sephora.Util.getUserInfoCache();

        let shouldGetUserFull = false;
        let userFullOptions = undefined;

        let apiToInclude;
        if (Sephora.Util.shouldGetTargeters() && Sephora.Util.shouldGetTargetedPromotion()) {
            apiToInclude = ['targetedPromotion'];
            userFullOptions = { includeApis: apiToInclude };
            shouldGetUserFull = true;
        }

        if (Sephora.Util.shouldGetUserFull(basketCache, currentUser, userInfoCache)) {
            shouldGetUserFull = true;
            if (!userFullOptions) {
                userFullOptions = { includeApis: [] };
            }
            userFullOptions.includeApis.push('profile', 'basket', 'loves', 'shoppingList');
        }

        if (shouldGetUserFull) {
            store.dispatch(UserActions.getUserFull(
                productPageData,
                null,
                userFullOptions
                    ? {
                        ...userFullOptions,
                        includeApis: userFullOptions.includeApis.join(',')
                    }
                    : undefined,
                undefined,
                options.hideLoader || false
            ));
        }
    },

    resetCertona: function () {
        require('services/Certona')();
    },

    resetDigitalData: function () {
        digitalData.lastRefresh = 'soft';
        digitalData.page.attributes.featureVariantKeys = [];
        digitalData.page.attributes.previousPageData.recInfo = {};
        digitalData.page.renderTime = null;
        digitalData.page.SpaApiCallTime = null;
        digitalData.page.startSpaLoad = performance.now();
        digitalData.performance.renderTime = null;
        digitalData.product = [];
    },

    resetTestAndTarget: function () {
        const Events = require('utils/framework/Events');
        Sephora.Util.InflatorComps.services.loadEvents[Events.TestTargetServiceReady] = false;
        require('services/TestTarget')();
    },

    resetAddStructure: function () {
        require('services/AddStructure')();
    },

    updateHeaderTags: (data, nextPageTemplate) => {
        const template = store.getState().page?.templateInformation?.template;
        const templateToCheck = nextPageTemplate || template;
        const isBuyPage = templateToCheck === PageTemplateType.RwdBuyPage;
        const isProductPage = templateToCheck === PageTemplateType.ProductPage;
        const seoParams = isProductPage ? data?.content : isBuyPage ? data?.rawBuyPageInfo : data;

        if (!seoParams) {
            return;
        }

        let {
            seoCanonicalUrl,
            seoMetaDescription,
            seoTitle
        } = seoParams;
        const customSeoTitle = capitalize(data?.rawBuyPageInfo?.slug || '');

        if (isBuyPage) {
            seoTitle = `${customSeoTitle} | Sephora`;
            seoMetaDescription = `${customSeoTitle} are available now at Sephora! Shop ${customSeoTitle} and find the best fit for your beauty routine. Free shipping and samples available.`;
            seoCanonicalUrl = `/buy/${customSeoTitle}`;
        }

        document.title = SpaUtils.getPageTitle(seoTitle, template);

        if (seoTitle) {
            document.getElementById('metaOgTitle')?.setAttribute('content', seoTitle);
        }

        if (seoMetaDescription) {
            document.getElementById('metaDescription')?.setAttribute('content', seoMetaDescription);
            document.getElementById('metaOgDescription')?.setAttribute('content', seoMetaDescription);
        }

        if (seoCanonicalUrl) {
            const url = UrlUtils.getLink(seoCanonicalUrl && DESKTOP + seoCanonicalUrl);
            document.getElementById('seoCanonicalUrl')?.setAttribute('href', url);
            document.getElementById('metaOgUrl')?.setAttribute('content', url);
        }
    },

    getPageTitle: (seoTitle, template) => {
        let title = 'Sephora';

        if (seoTitle) {
            title = seoTitle;
        } else if (LocationUtils.getPageTitle(template)) {
            const postfix = template.indexOf('Community') === -1 ? ' | Sephora' : '';
            title = `${LocationUtils.getPageTitle(template)}${postfix}`;
        }

        return title;
    },

    isSpaNavigation: nextPageUrl =>
        Sephora.isSPA &&
        Sephora.configurationSettings.spaEnabled &&
        SpaUtils.currentPageIsActiveSpaPage() &&
        SpaUtils.nextPageIsActiveSpaPage(nextPageUrl),

    currentPageIsActiveSpaPage: () => {
        const { page: { templateInformation: { template } } } = store.getState();
        const templateInfo = SpaUtils.getSpaTemplateInfoByTemplate(template);
        return SpaUtils.getKillSwitchValueOrDefault(templateInfo, false);
    },

    nextPageIsActiveSpaPage: (nextPageUrl) => {
        const templateInfo = SpaUtils.getSpaTemplateInfoByUrl(nextPageUrl);
        return SpaUtils.getKillSwitchValueOrDefault(templateInfo, false);
    },

    getKillSwitchValueOrDefault: (templateInfo, defaultValue) => {
        const killSwitchName = templateInfo?.killSwitchName;
        const result = killSwitchName && Object.prototype.hasOwnProperty.call(Sephora.configurationSettings, killSwitchName) ?
            Sephora.configurationSettings[killSwitchName] : killSwitchName === undefined ? false : defaultValue;
        return result;
    },

    getSpaTemplateInfoByTemplate: template => SpaTemplatesInfo.find(x => x.template === template),

    getSpaTemplateInfoByUrl: pageUrl => {
        // (EXP-3716) Special case home path as everything startsWith '/'
        const matchNullPath = ({ url }) => url === '/';
        if (pageUrl === '/') {
            return SpaTemplatesInfo.find(matchNullPath);
        }

        return reject(SpaTemplatesInfo, matchNullPath)
            .find(info => {
                return pageUrl.startsWith(info.url) ||
                    pageUrl.startsWith(`/ca/fr${info.url}`) ||
                    pageUrl.startsWith(`/ca/en${info.url}`);
            });
    },

    isSpaTemplate: template => SpaTemplatesInfo.some(x => x.template === template)

};

module.exports = SpaUtils;
